import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { BarChart3, Bot, PhoneOutgoing, ShieldCheck } from 'lucide-react';
import { Button, Field, Input } from '@ui/components/ui';
import { errorMessage } from '@ui/lib/errors';
import { gsap, reduced } from '@ui/lib/motion';
import { useForgotPasswordMutation, useLoginMutation, useMeQuery, useResetPasswordMutation } from '@ui/store/authApi';
import type { Session } from '@ui/store/types';

/** Sign-in screens shared by the customer app and the admin panel. */

/** A voice waveform drawn in bars, animated with GSAP: the product, in one picture. */
function Waveform() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ref.current || reduced()) return;
    const bars = ref.current.querySelectorAll('span');
    const tween = gsap.to(bars, { scaleY: () => 0.25 + Math.random() * 0.9, duration: 0.5, ease: 'sine.inOut', stagger: { each: 0.04, repeat: -1, yoyo: true } });
    return () => {
      tween.kill();
    };
  }, []);
  return (
    <div ref={ref} className="flex h-16 items-center gap-[5px]">
      {Array.from({ length: 36 }).map((_, i) => (
        <span key={i} className="block w-[4px] origin-center rounded-full bg-white/70" style={{ height: `${30 + ((i * 37) % 70)}%` }} />
      ))}
    </div>
  );
}

export function AuthLayout({ title, subtitle, children, footer }: { title: string; subtitle?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  const { data } = useMeQuery();
  const appName = data?.platform.appName ?? 'Dialwise';
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!panel.current || reduced()) return;
    const ctx = gsap.context(() => gsap.from('.reveal', { y: 14, opacity: 0, duration: 0.6, ease: 'power3.out', stagger: 0.08 }), panel);
    return () => ctx.revert();
  }, []);
  return (
    <div className="grid min-h-full lg:grid-cols-[1.05fr_1fr]">
      <div ref={panel} className="relative hidden overflow-hidden bg-[#0b0a24] p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="absolute -left-32 -top-32 h-[520px] w-[520px] rounded-full bg-[#635bff] opacity-40 blur-[120px]" />
        <div className="absolute -bottom-40 right-[-120px] h-[460px] w-[460px] rounded-full bg-[#00d4ff] opacity-25 blur-[120px]" />
        <div className="reveal relative flex items-center gap-2 text-lg font-semibold">
          <img src="/favicon.svg" alt="" className="h-8 w-8" /> {appName}
        </div>
        <div className="relative space-y-8">
          <div className="reveal">
            <h2 className="max-w-md text-4xl font-semibold leading-tight tracking-tight">AI voice agents that call, qualify and follow up.</h2>
            <p className="mt-4 max-w-md text-base text-white/70">Upload a list, pick a voice, launch. Every call recorded, transcribed and scored.</p>
          </div>
          <div className="reveal">
            <Waveform />
          </div>
          <div className="reveal grid max-w-md grid-cols-2 gap-4 text-sm text-white/80">
            {[
              [PhoneOutgoing, 'Campaigns at scale'],
              [Bot, 'Any industry, any script'],
              [BarChart3, 'Live results per call'],
              [ShieldCheck, 'Full audit trail']
            ].map(([Icon, label]) => {
              const I = Icon as typeof Bot;
              return (
                <div key={label as string} className="flex items-center gap-2">
                  <I className="h-4 w-4 text-[#a5a0ff]" /> {label as string}
                </div>
              );
            })}
          </div>
        </div>
        <p className="reveal relative text-xs text-white/40">© {new Date().getFullYear()} {appName}</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <img src="/favicon.svg" alt="" className="h-8 w-8" />
            <span className="text-lg font-semibold">{appName}</span>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {subtitle && <p className="mt-1.5 text-sm text-muted">{subtitle}</p>}
          <div className="mt-7">{children}</div>
          {footer && <div className="mt-6 text-center text-sm text-muted">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  return <div className="rounded-md border border-bad/25 bg-bad-soft px-3 py-2 text-sm text-bad">{errorMessage(error)}</div>;
}

export interface LoginPageProps {
  title?: string;
  subtitle?: string;
  /** Under the form, e.g. a sign-up link. */
  footer?: ReactNode;
  /** Where to go once signed in (unless a ?next= is present). */
  destination: (session: Session) => string;
}

export function LoginPage({ title = 'Sign in', subtitle = 'Welcome back. Enter your details to continue.', footer, destination }: LoginPageProps) {
  const [login, { isLoading, error }] = useLoginMutation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const session = await login({ email, password }).unwrap().catch(() => null);
    if (!session) return;
    const target = params.get('next') ?? destination(session);
    if (/^https?:\/\//.test(target)) window.location.assign(target);
    else navigate(target);
  };
  return (
    <AuthLayout title={title} subtitle={subtitle} footer={footer}>
      <form onSubmit={submit} className="space-y-4">
        <ErrorBox error={error} />
        <Field label="Email">
          <Input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
        </Field>
        <Field label={<span className="flex justify-between">Password <Link to="/forgot-password" className="link font-normal">Forgot?</Link></span>}>
          <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </Field>
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={isLoading}>
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const [forgot, { isLoading, isSuccess, error }] = useForgotPasswordMutation();
  const [email, setEmail] = useState('');
  return (
    <AuthLayout title="Reset your password" subtitle="We will email you a link to choose a new one." footer={<Link to="/login" className="link">Back to sign in</Link>}>
      {isSuccess ? (
        <div className="rounded-md border border-good/25 bg-good-soft px-3 py-3 text-sm text-good">If that email is registered, a reset link is on its way.</div>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); void forgot({ email }); }} className="space-y-4">
          <ErrorBox error={error} />
          <Field label="Email"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus /></Field>
          <Button type="submit" variant="primary" size="lg" className="w-full" loading={isLoading}>Send reset link</Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const [reset, { isLoading, isSuccess, error }] = useResetPasswordMutation();
  const [password, setPassword] = useState('');
  return (
    <AuthLayout title="Choose a new password" footer={<Link to="/login" className="link">Back to sign in</Link>}>
      {isSuccess ? (
        <div className="rounded-md border border-good/25 bg-good-soft px-3 py-3 text-sm text-good">Password updated. <Link to="/login" className="link">Sign in</Link></div>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); void reset({ token: params.get('token') ?? '', password }); }} className="space-y-4">
          <ErrorBox error={error} />
          <Field label="New password" hint="At least 10 characters"><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={10} required autoFocus /></Field>
          <Button type="submit" variant="primary" size="lg" className="w-full" loading={isLoading}>Update password</Button>
        </form>
      )}
    </AuthLayout>
  );
}

